import { z } from 'zod';
import { AssetSchema } from '../shared/asset.schema';
const VideoGenerationWidgetRunwayGen4InputSchema = z.object({
    model: z.literal('runway-gen-4').describe('The AI provider to use for video generation'),
    aspectRatio: z
        .enum(['1280:720', '720:1280', '1104:832', '832:1104', '960:960', '1584:672'])
        .optional()
        .default('1280:720'),
    startFrame: AssetSchema.optional()
        .nullable()
        .describe('The frame to start the video generation from'),
});
const VideoGenerationWidgetRunwayGen45InputSchema = z.object({
    model: z.literal('runway-gen-4.5').describe('The AI provider to use for video generation'),
    durationSeconds: z
        .number()
        .min(2)
        .max(10)
        .optional()
        .default(5)
        .describe('Duration of the generated video in seconds (2-10)'),
    aspectRatio: z
        .enum(['1280:720', '720:1280', '1104:832', '832:1104', '960:960', '1584:672'])
        .optional()
        .default('1280:720'),
    startFrame: AssetSchema.optional()
        .nullable()
        .describe('The frame to start the video generation from'),
});
/** Veo 3.1 and its Fast tier take the same request shape, so they differ only by the literal. */
const veo31Settings = {
    durationSeconds: z
        .number()
        .min(4)
        .max(8)
        .optional()
        .default(8)
        .describe('Duration of the generated video in seconds (4-8)'),
    resolution: z.enum(['720p', '1080p']).optional().default('720p'),
    aspectRatio: z.enum(['16:9', '9:16']).optional().default('16:9'),
    startFrame: AssetSchema.optional()
        .nullable()
        .describe('The frame to start the video generation from'),
    endFrame: AssetSchema.optional().nullable().describe('The frame to end the video generation at'),
};
const VideoGenerationWidgetGeminiVeo31InputSchema = z.object({
    model: z.literal('veo-3.1').describe('The AI provider to use for video generation'),
    ...veo31Settings,
});
const VideoGenerationWidgetGeminiVeo31FastInputSchema = z.object({
    model: z.literal('veo-3.1-fast').describe('The AI provider to use for video generation'),
    ...veo31Settings,
});
// This tier was called `veo-3.1-lite` while it pointed at Veo's Fast model. The literal stays in
// the union because stored chat options still carry it; `normalizeVideoGenerationInput` maps it
// onto `veo-3.1-fast` before a generation runs.
const VideoGenerationWidgetGeminiVeo31LiteInputSchema = z.object({
    model: z.literal('veo-3.1-lite').describe('The AI provider to use for video generation'),
    ...veo31Settings,
});
const VideoGenerationWidgetGeminiOmniInputSchema = z.object({
    model: z.literal('gemini-omni-1.1-flash').describe('The AI provider to use for video generation'),
    durationSeconds: z
        .number()
        .int()
        .min(3)
        .max(10)
        .optional()
        .default(8)
        .describe('Duration of the generated video in seconds (3-10)'),
    resolution: z.enum(['720p', '1080p']).optional().default('720p'),
    aspectRatio: z.enum(['16:9', '9:16']).optional().default('16:9'),
    startFrame: AssetSchema.optional()
        .nullable()
        .describe('The frame to start the video generation from'),
    endFrame: AssetSchema.optional()
        .nullable()
        .describe('The frame to end the video generation at; requires a start frame'),
});
const VideoGenerationWidgetInputObject = {
    prompt: z.string().describe('The text prompt describing the desired video'),
    settings: z
        .discriminatedUnion('model', [
        VideoGenerationWidgetGeminiVeo31InputSchema,
        VideoGenerationWidgetGeminiVeo31FastInputSchema,
        VideoGenerationWidgetGeminiVeo31LiteInputSchema,
        VideoGenerationWidgetGeminiOmniInputSchema,
        VideoGenerationWidgetRunwayGen4InputSchema,
        VideoGenerationWidgetRunwayGen45InputSchema,
    ])
        .optional()
        .describe('Settings for video generation, including model and parameters')
        .default({
        model: 'veo-3.1',
        durationSeconds: 8,
        resolution: '720p',
        aspectRatio: '16:9',
    }),
};
export const VideoGenerationWidgetInputSchema = z.object(VideoGenerationWidgetInputObject);
/** A Map, not an object literal, so a model id that collides with an Object prototype key misses. */
export const DEPRECATED_VIDEO_MODEL_REPLACEMENTS = new Map([['veo-3.1-lite', 'veo-3.1-fast']]);
/**
 * Redirects a renamed model to its current id before generation. Apply it to generation input
 * only, never to a saved widget being read back: that widget records which model actually
 * produced the video, and rewriting it would misattribute history.
 */
export function normalizeVideoGenerationInput(input) {
    const replacement = DEPRECATED_VIDEO_MODEL_REPLACEMENTS.get(input.settings.model);
    if (!replacement) {
        return input;
    }
    return VideoGenerationWidgetInputSchema.parse({
        ...input,
        settings: { ...input.settings, model: replacement },
    });
}
export const VideoGenerationWidgetSchemaKey = 'video-generation-widget:v1';
export const VideoGenerationWidgetSchemaKind = 'video';
/** Server-owned key prefix for saved video-generation outputs. */
export const SAVED_VIDEO_ARTIFACT_KEY_PREFIX = 'saved-video:';
export const VideoGenerationWidgetSchema = z.object({
    version: z.literal(1).describe('Version of the image generation widget schema'),
    title: z.string().describe('Title of the video widget'),
    input: z
        .object({
        ...VideoGenerationWidgetInputObject,
        files: z.array(AssetSchema).optional(),
    })
        .describe('Input settings for video generation'),
    video: AssetSchema.describe('The generated video asset'),
    createdAt: z.date().describe('Timestamp when the video was initially generated'),
});
